import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { NEET_PHYSICS_CH3_QUESTIONS } from "../data/neetPhysicsCh3Questions";

// ─────────────────────────────────────────────────────────────────────────────
// NEET Physics — Chapter 3: Motion in a Plane (NCERT Class 11).
// Content is drafted from the NCERT syllabus topic list; each concept has an
// explanation plus a worked example. NOT YET human-reviewed by a subject-matter
// expert — treat as a first draft and verify against NCERT before exam use.
// ─────────────────────────────────────────────────────────────────────────────

const SECTIONS = ["Vectors & Vector Algebra", "Vector Resolution & Relative Motion", "Projectile Motion", "Uniform Circular Motion"];

const SECTION_COLORS = {
  "Vectors & Vector Algebra":              "#2563eb",
  "Vector Resolution & Relative Motion":   "#7c3aed",
  "Projectile Motion":                     "#dc2626",
  "Uniform Circular Motion":               "#16a34a",
};

const CONCEPTS = [
  // ── Vectors & Vector Algebra ────────────────────────────────────────────────
  { section: "Vectors & Vector Algebra", title: "Scalar and vector quantities",
    explanation: "A scalar quantity is completely specified by its magnitude alone (with a unit) -- e.g. mass, distance, speed, temperature, energy. A vector quantity needs both magnitude AND direction to be fully specified -- e.g. displacement, velocity, acceleration, force -- and vectors combine using the geometrical laws of vector addition (triangle/parallelogram law), NOT ordinary algebra.",
    example: `Scalars: mass, distance, speed, work, temperature
Vectors: displacement, velocity, acceleration, force

5 kg + 3 kg = 8 kg               (scalars: simple arithmetic)
5 m (east) + 3 m (north) != 8 m  (vectors: need vector addition)` },
  { section: "Vectors & Vector Algebra", title: "Triangle law of vector addition",
    explanation: "If two vectors are represented in magnitude and direction by two sides of a triangle taken in the same order (tip of the first to tail of the second), their resultant is represented by the third side of the triangle taken in the REVERSE order (from the tail of the first to the tip of the second).",
    example: `A = 3 units (east), B = 4 units (north), A perpendicular to B
Resultant R = sqrt(A^2 + B^2) = sqrt(3^2 + 4^2) = sqrt(25) = 5 units

tan(theta) = B/A = 4/3  ->  theta = 53.1 deg from east (A's direction)` },
  { section: "Vectors & Vector Algebra", title: "Parallelogram law of vector addition",
    explanation: "If two vectors are represented by two adjacent sides of a parallelogram drawn from a common point, their resultant is represented by the diagonal of the parallelogram passing through that same point. Its magnitude is R = sqrt(A^2 + B^2 + 2AB cos(theta)), where theta is the angle between A and B; its direction relative to A satisfies tan(alpha) = B sin(theta) / (A + B cos(theta)).",
    example: `A = 4 N, B = 4 N, theta between them = 120 deg

R = sqrt(4^2 + 4^2 + 2(4)(4)cos120)
  = sqrt(16 + 16 + 32(-0.5)) = sqrt(32 - 16) = sqrt(16) = 4 N

tan(alpha) = 4 sin120 / (4 + 4 cos120) = 3.46 / 2 = 1.73 -> alpha = 60 deg` },
  { section: "Vectors & Vector Algebra", title: "Vector subtraction",
    explanation: "Subtracting vector B from vector A means adding the negative of B (a vector of the same magnitude as B but pointing in the opposite direction): A - B = A + (-B). Its magnitude is R = sqrt(A^2 + B^2 - 2AB cos(theta)), where theta is the angle between the ORIGINAL A and B.",
    example: `A = 5 units, B = 5 units, theta between them = 60 deg

|A - B| = sqrt(5^2 + 5^2 - 2(5)(5)cos60)
        = sqrt(25 + 25 - 50(0.5)) = sqrt(50 - 25) = sqrt(25) = 5 units` },

  // ── Vector Resolution & Relative Motion ─────────────────────────────────────
  { section: "Vector Resolution & Relative Motion", title: "Resolution of a vector into rectangular components",
    explanation: "Any vector in a plane can be resolved into two mutually perpendicular components along the x- and y-axes. If a vector A makes angle theta with the x-axis, its components are Ax = A cos(theta) and Ay = A sin(theta), and A can be recovered from A = sqrt(Ax^2 + Ay^2), tan(theta) = Ay/Ax.",
    example: `A = 10 units, theta = 30 deg with the x-axis

Ax = A cos(theta) = 10 x cos30 = 10 x 0.866 = 8.66 units
Ay = A sin(theta) = 10 x sin30 = 10 x 0.5   = 5.00 units

Check: sqrt(Ax^2 + Ay^2) = sqrt(8.66^2 + 5^2) = sqrt(75+25) = 10 units` },
  { section: "Vector Resolution & Relative Motion", title: "Unit vectors i, j, k and vector representation",
    explanation: "A unit vector has magnitude 1 and only indicates direction. i, j, k are unit vectors along the x, y, z axes. Any vector A can be written as A = Ax i + Ay j + Az k, with magnitude |A| = sqrt(Ax^2 + Ay^2 + Az^2). The unit vector along A itself is A^ = A / |A|.",
    example: `A = 3i + 4j

|A| = sqrt(3^2 + 4^2) = sqrt(9+16) = sqrt(25) = 5

Unit vector along A:  A^ = A / |A| = (3i + 4j)/5 = 0.6i + 0.8j` },
  { section: "Vector Resolution & Relative Motion", title: "Position and displacement vectors",
    explanation: "The position vector r locates a particle relative to a chosen origin: r = x i + y j. If a particle moves from position r1 to r2, its displacement vector is Delta r = r2 - r1 -- pointing from the initial to the final position, independent of the actual path taken.",
    example: `r1 = 2i + 3j m   (initial position)
r2 = 5i + 7j m   (final position)

Displacement:  Delta r = r2 - r1 = (5-2)i + (7-3)j = 3i + 4j m
|Delta r| = sqrt(3^2 + 4^2) = 5 m` },
  { section: "Vector Resolution & Relative Motion", title: "Relative velocity in two dimensions",
    explanation: "The velocity of object A relative to object B is v(AB) = v(A) - v(B), found by ordinary vector subtraction. This is why, for example, vertically falling rain appears to a walking observer to come from a slanted direction -- the rain's velocity relative to the observer is the vector difference of the rain's velocity and the observer's velocity.",
    example: `Rain falls straight down at 8 m/s; a man walks horizontally at 6 m/s.
v(rain) = -8j,  v(man) = 6i     (down = -j, his walking direction = +i)

v(rain rel. man) = v(rain) - v(man) = -6i - 8j
|v(rain rel. man)| = sqrt(6^2 + 8^2) = sqrt(100) = 10 m/s

tan(theta) = 6/8 = 0.75 -> theta = 37 deg from the vertical
(he should tilt his umbrella forward by about 37 deg)` },

  // ── Projectile Motion ────────────────────────────────────────────────────────
  { section: "Projectile Motion", title: "Trajectory of a projectile — the parabola equation",
    explanation: "For a projectile launched with speed u at angle theta above the horizontal, eliminating time between x = (u cos theta)t and y = (u sin theta)t - (1/2)g t^2 gives y = x tan(theta) - g x^2 / (2 u^2 cos^2(theta)). Since y is proportional to x and to x^2 (with no higher powers), this is the equation of a PARABOLA.",
    example: `u = 10 m/s, theta = 45 deg, g = 10 m/s^2
u cos45 = u sin45 = 7.07 m/s

y = x tan45 - (10 x^2)/(2 x 10^2 x cos^2 45)
  = x - (10 x^2)/(2 x 100 x 0.5) = x - 0.1x^2

At x = 5 m:  y = 5 - 0.1(25) = 5 - 2.5 = 2.5 m` },
  { section: "Projectile Motion", title: "Time of flight of a projectile",
    explanation: "Time of flight T is the total time the projectile stays in the air, found by setting the net vertical displacement to zero (for launch and landing at the same height): T = 2 u sin(theta) / g. It depends only on the vertical component of the initial velocity and on g -- not on mass.",
    example: `u = 20 m/s, theta = 30 deg, g = 10 m/s^2

T = 2 u sin(theta) / g = 2(20)(sin30) / 10 = 2(20)(0.5)/10 = 20/10 = 2 s` },
  { section: "Projectile Motion", title: "Maximum height of a projectile",
    explanation: "Maximum height H is reached when the vertical component of velocity becomes zero: H = u^2 sin^2(theta) / (2g). It increases with the angle of projection theta, becoming largest (for fixed u) at theta = 90 deg (a straight vertical throw).",
    example: `u = 20 m/s, theta = 30 deg, g = 10 m/s^2

H = u^2 sin^2(theta) / (2g) = (20^2)(sin30)^2 / (2x10)
  = 400 x 0.25 / 20 = 100/20 = 5 m` },
  { section: "Projectile Motion", title: "Horizontal range and the range formula",
    explanation: "Horizontal range R is the horizontal distance covered in the time of flight: R = (u cos theta)(T) = u^2 sin(2 theta) / g. Since sin(2 theta) is maximum (=1) at 2 theta = 90 deg, the range is MAXIMUM at theta = 45 deg, giving R(max) = u^2/g. Also, since sin(2theta) = sin(180 - 2theta), complementary angles theta and (90 - theta) give the SAME range for the same u.",
    example: `u = 20 m/s, theta = 30 deg, g = 10 m/s^2

R = u^2 sin(2 theta)/g = 400 x sin60 / 10 = 400 x 0.866/10 = 34.6 m

At theta = 45 deg (same u):
R(max) = u^2/g = 400/10 = 40 m   <- greatest possible range for this u

Note: theta = 30 deg and theta = 60 deg give the SAME range (34.6 m)
since sin60 deg = sin120 deg.` },

  // ── Uniform Circular Motion ──────────────────────────────────────────────────
  { section: "Uniform Circular Motion", title: "Angular velocity and time period",
    explanation: "In uniform circular motion, angular velocity omega = d theta/dt is the rate of change of angular position, related to the time period T (time for one full revolution) and frequency f by omega = 2*pi/T = 2*pi*f. The linear (tangential) speed is v = omega * r.",
    example: `Radius r = 2 m, Time period T = pi seconds

omega = 2*pi / T = 2*pi / pi = 2 rad/s
v = omega x r = 2 x 2 = 4 m/s` },
  { section: "Uniform Circular Motion", title: "Centripetal acceleration",
    explanation: "Even though speed stays constant in uniform circular motion, the DIRECTION of velocity keeps changing, so the motion is accelerated. This centripetal acceleration always points toward the centre of the circle, with magnitude a = v^2/r = omega^2 * r.",
    example: `Using v = 4 m/s, r = 2 m, omega = 2 rad/s (from above)

a = v^2/r = 4^2/2 = 16/2 = 8 m/s^2
Check: a = omega^2 x r = 2^2 x 2 = 4 x 2 = 8 m/s^2   (matches)` },
  { section: "Uniform Circular Motion", title: "Centripetal force",
    explanation: "By Newton's second law, a centripetal acceleration requires a net centripetal FORCE directed toward the centre: F = m v^2/r = m omega^2 r. This is not a new kind of force -- it is whatever real force (tension, gravity, friction, normal reaction) happens to supply the required centre-directed net force.",
    example: `m = 0.5 kg, v = 4 m/s, r = 2 m

F = m v^2/r = 0.5 x 4^2 / 2 = 0.5 x 16/2 = 8/2 = 4 N
(directed towards the centre of the circular path)` },
  { section: "Uniform Circular Motion", title: "Velocity and acceleration vectors in uniform circular motion",
    explanation: "Describing the position on a circle of radius r as r(t) = r cos(omega t) i + r sin(omega t) j, differentiating gives velocity v(t) = -r omega sin(omega t) i + r omega cos(omega t) j, which is always tangential (perpendicular to r) with constant magnitude r*omega. Differentiating again gives acceleration a(t) = -omega^2 [r cos(omega t) i + r sin(omega t) j] = -omega^2 r(t) -- exactly opposite to the position vector, i.e. always pointing toward the centre.",
    example: `r(t) = r cos(wt) i + r sin(wt) j
v(t) = dr/dt = -r.w.sin(wt) i + r.w.cos(wt) j   -> |v| = r.w  (constant)
a(t) = dv/dt = -w^2 [r cos(wt) i + r sin(wt) j] = -w^2 . r(t)

a(t) points opposite to r(t) -> always directed toward the centre` },
];

const S = {
  page:  { minHeight:"100vh", background:"linear-gradient(135deg,#0f172a,#1a1a2e)", fontFamily:"'Segoe UI',sans-serif", color:"#e2e8f0", padding:"18px 16px 80px",
    userSelect:"none", WebkitUserSelect:"none", MozUserSelect:"none", msUserSelect:"none" },
  back:  { color:"#94a3b8", fontSize:13, textDecoration:"none" },
  card:  { background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.11)", borderRadius:14, padding:"18px", marginBottom:16 },
  chip:  (active, color) => ({
    background: active ? color : "rgba(255,255,255,0.08)",
    color: active ? "#fff" : "#94a3b8",
    border:"none", borderRadius:20, padding:"6px 14px", fontSize:12.5, fontWeight:700, cursor:"pointer", marginRight:8, marginBottom:8,
  }),
  tag: (color) => ({ background:color, color:"#fff", borderRadius:20, padding:"2px 10px", fontSize:11, fontWeight:700, display:"inline-block" }),
  pre: { background:"#0b1120", border:"1px solid rgba(255,255,255,0.08)", borderRadius:10, padding:"12px 14px", fontSize:12.5, lineHeight:1.6, color:"#a5f3b8", overflowX:"auto", fontFamily:"'Fira Code','Consolas',monospace", whiteSpace:"pre" },
  option: (bg) => ({ display:"block", width:"100%", textAlign:"left", background:bg, border:"1px solid rgba(255,255,255,0.1)", borderRadius:8, padding:"9px 12px", color:"#e2e8f0", fontSize:13, marginBottom:7, cursor:"pointer" }),
  iconBtn: (bg) => ({ background:bg, border:"none", borderRadius:8, width:30, height:30, fontSize:14, lineHeight:1, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", flex:"none" }),
};

// ── One interactive objective question — click an option to check it ──────────
function QuizQuestion({ item, index, onAnswer }) {
  const [selected, setSelected] = useState(null);
  const answered = selected !== null;

  const pick = (i) => {
    if (answered) return;
    setSelected(i);
    onAnswer && onAnswer(i === item.correct);
  };

  return (
    <div style={S.card}>
      <span style={S.tag(SECTION_COLORS[item.section])}>{item.section}</span>
      <span style={{ fontSize:11, color:"#64748b", marginLeft:8 }}>{item.topic}</span>
      <div style={{ fontWeight:700, fontSize:14, color:"#fff", marginTop:8, marginBottom:10 }}>{index + 1}. {item.q}</div>
      {item.options.map((opt, i) => {
        let bg = "rgba(255,255,255,0.05)";
        if (answered) {
          if (i === item.correct) bg = "rgba(34,197,94,0.28)";
          else if (i === selected) bg = "rgba(239,68,68,0.28)";
        }
        return (
          <button key={i} style={S.option(bg)} onClick={() => pick(i)} disabled={answered}>
            {String.fromCharCode(65 + i)}. {opt}
          </button>
        );
      })}
      {answered && (
        <div style={{ fontSize:12.5, color:"#94a3b8", marginTop:8, lineHeight:1.6 }}>
          {selected === item.correct ? "✅ Correct — " : "❌ Not quite — "}{item.explain}
        </div>
      )}
    </div>
  );
}

export default function NeetPhysicsCh3Page() {
  const [mode, setMode] = useState("guide"); // "guide" | "practice"
  const [activeSection, setActiveSection] = useState("All");
  const [score, setScore] = useState({ correct: 0, answered: 0 });

  // ── Bookmarks — same 🔖 pattern used elsewhere on the site, persisted locally ──
  const [bookmarks, setBookmarks] = useState(() => {
    try {
      const raw = window.localStorage.getItem("neetPhysicsCh3Bookmarks");
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [showBookmarkedOnly, setShowBookmarkedOnly] = useState(false);

  useEffect(() => {
    try { window.localStorage.setItem("neetPhysicsCh3Bookmarks", JSON.stringify(bookmarks)); } catch { /* ignore */ }
  }, [bookmarks]);

  const toggleBookmark = (title) => {
    setBookmarks(prev => prev.includes(title) ? prev.filter(t => t !== title) : [...prev, title]);
  };

  // ── Read aloud — same speechSynthesis pattern used on Reading Notes / TNPSC ──
  const [speakingIdx, setSpeakingIdx] = useState(null);

  useEffect(() => () => { window.speechSynthesis && window.speechSynthesis.cancel(); }, []);

  const speakConcept = (c, idx) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    if (speakingIdx === idx) { setSpeakingIdx(null); return; }
    const u = new window.SpeechSynthesisUtterance(`${c.title}. ${c.explanation}`);
    u.rate = 0.95;
    u.onend = () => setSpeakingIdx(null);
    u.onerror = () => setSpeakingIdx(null);
    window.speechSynthesis.speak(u);
    setSpeakingIdx(idx);
  };

  // ── Share — native share sheet, falling back to WhatsApp like the other pages ──
  const shareConcept = (c, idx) => {
    const url = `${window.location.origin}${window.location.pathname}#concept-${idx}`;
    const text = `🧪 NEET Physics — ${c.title}\n\n${c.explanation}\n\n${url}`;
    if (navigator.share) {
      navigator.share({ title: `NEET Physics — ${c.title}`, text, url }).catch(() => {});
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
    }
  };

  const filtered = useMemo(() => {
    let list = activeSection === "All" ? CONCEPTS : CONCEPTS.filter(c => c.section === activeSection);
    if (showBookmarkedOnly) list = list.filter(c => bookmarks.includes(c.title));
    return list;
  }, [activeSection, showBookmarkedOnly, bookmarks]);

  const filteredQuestions = useMemo(() => (
    activeSection === "All" ? NEET_PHYSICS_CH3_QUESTIONS : NEET_PHYSICS_CH3_QUESTIONS.filter(q => q.section === activeSection)
  ), [activeSection]);

  const handleModeChange = (m) => {
    setMode(m);
    setScore({ correct: 0, answered: 0 });
  };

  const handleAnswer = (isCorrect) => {
    setScore(s => ({ correct: s.correct + (isCorrect ? 1 : 0), answered: s.answered + 1 }));
  };

  return (
    <div
      style={S.page}
      onCopy={(e) => e.preventDefault()}
      onCut={(e) => e.preventDefault()}
      onContextMenu={(e) => e.preventDefault()}
    >
      <Link to="/neet-physics" style={S.back}>← Back to NEET Physics Menu</Link>

      <div style={{ textAlign:"center", padding:"14px 0 10px" }}>
        <div style={{ fontSize:26, fontWeight:800, color:"#fff" }}>🧪 NEET Physics — Chapter 3</div>
        <div style={{ fontSize:15, fontWeight:700, color:"#fbbf24", marginTop:4 }}>Motion in a Plane</div>
        <div style={{ fontSize:12.5, color:"#94a3b8", marginTop:6 }}>
          NCERT Class 11, Ch. 3 · {CONCEPTS.length} concepts explained · {NEET_PHYSICS_CH3_QUESTIONS.length} practice questions · NOT YET human-reviewed, verify against NCERT before exam use
        </div>
      </div>

      {/* Guide / Practice mode toggle */}
      <div style={{ textAlign:"center", margin:"14px 0 8px" }}>
        <button style={S.chip(mode==="guide", "#0f766e")} onClick={() => handleModeChange("guide")}>📘 Concept Guide</button>
        <button style={S.chip(mode==="practice", "#b45309")} onClick={() => handleModeChange("practice")}>🎯 Practice Questions ({NEET_PHYSICS_CH3_QUESTIONS.length})</button>
      </div>

      {/* Section filter chips */}
      <div style={{ textAlign:"center", margin:"6px 0 18px" }}>
        <button style={S.chip(activeSection==="All", "#334155")} onClick={() => setActiveSection("All")}>All</button>
        {SECTIONS.map(s => (
          <button key={s} style={S.chip(activeSection===s, SECTION_COLORS[s])} onClick={() => setActiveSection(s)}>{s}</button>
        ))}
      </div>

      {mode === "practice" ? (
        <>
          {score.answered > 0 && (
            <div style={{ textAlign:"center", fontSize:13, fontWeight:700, color:"#fbbf24", marginBottom:14 }}>
              Score: {score.correct} / {score.answered} answered correctly
            </div>
          )}
          {filteredQuestions.map((item, i) => (
            <QuizQuestion key={i} item={item} index={i} onAnswer={handleAnswer} />
          ))}
        </>
      ) : (
      <>
      {/* ── Bookmark filter ── */}
      <div style={{ textAlign:"center", margin:"0 0 14px" }}>
        <button style={S.chip(showBookmarkedOnly, "#db2777")} onClick={() => setShowBookmarkedOnly(v => !v)}>
          🔖 {showBookmarkedOnly ? "Showing Bookmarked" : "Bookmarked"} ({bookmarks.length})
        </button>
      </div>

      {/* ── Quick reference table ── */}
      <div style={S.card}>
        <div style={{ fontWeight:800, fontSize:16, color:"#fff", marginBottom:12 }}>📋 Quick Reference Table</div>
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
            <thead>
              <tr style={{ borderBottom:"2px solid rgba(255,255,255,0.15)" }}>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>#</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>Section</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>Topic</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c, i) => (
                <tr key={i} style={{ borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
                  <td style={{ padding:"7px 8px", color:"#64748b" }}>{i + 1}</td>
                  <td style={{ padding:"7px 8px" }}><span style={S.tag(SECTION_COLORS[c.section])}>{c.section}</span></td>
                  <td style={{ padding:"7px 8px" }}>
                    <a
                      href={`#concept-${i}`}
                      style={{ color:"#38bdf8", textDecoration:"underline", fontWeight:600 }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = "#7dd3fc"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = "#38bdf8"; }}
                    >
                      {c.title}
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Detailed concept cards ── */}
      {filtered.map((c, i) => {
        const isBookmarked = bookmarks.includes(c.title);
        const isSpeaking = speakingIdx === i;
        return (
        <div key={i} id={`concept-${i}`} style={S.card}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:8, flexWrap:"wrap" }}>
            <span style={S.tag(SECTION_COLORS[c.section])}>{c.section}</span>
            <div style={{ display:"flex", gap:6 }}>
              <button
                title={isSpeaking ? "Stop reading" : "Read aloud"}
                onClick={() => speakConcept(c, i)}
                style={S.iconBtn(isSpeaking ? "#2563eb" : "rgba(37,99,235,0.22)")}
              >{isSpeaking ? "⏹" : "🔊"}</button>
              <button
                title={isBookmarked ? "Remove bookmark" : "Bookmark"}
                onClick={() => toggleBookmark(c.title)}
                style={S.iconBtn(isBookmarked ? "#db2777" : "rgba(219,39,119,0.22)")}
              >{isBookmarked ? "🔖" : "📌"}</button>
              <button
                title="Share"
                onClick={() => shareConcept(c, i)}
                style={S.iconBtn("rgba(255,255,255,0.12)")}
              >↗</button>
            </div>
          </div>
          <div style={{ fontWeight:800, fontSize:16, color:"#fff", marginTop:8 }}>{i + 1}. {c.title}</div>
          <div style={{ fontSize:13.5, color:"#cbd5e1", lineHeight:1.7, marginTop:8 }}>{c.explanation}</div>
          <div style={{ fontSize:11, fontWeight:700, color:"#94a3b8", marginTop:12, marginBottom:6, letterSpacing:0.4 }}>EXAMPLE / WORKED CALCULATION</div>
          <pre style={S.pre}><code>{c.example}</code></pre>
        </div>
        );
      })}
      </>
      )}
    </div>
  );
}
